import {
  TRUNCATABLE_CELL_SELECTOR,
  computeTbodySlotContentKey,
  findElementsByColumnValue,
  findTruncatableTarget,
  getHeaderColumnStyle,
  getRowDataCells,
  getTruncatableDataHeaders,
  hasCustomTbodySlot,
  isTruncatedElement,
  resolveHeaderMaxWidthPx,
  shouldSkipTruncationElement,
} from './cellTruncationHelpers';
import { TRUNCATE_TEXT_ATTR } from './constants';

const TRUNCATION_REFRESH_MS = 50;
/** Delay before hiding the cell tooltip after mouse leave. */
const TOOLTIP_HIDE_DELAY_MS = 100;
/** Gap between tooltip and the hovered cell edge (below the text). */
const TOOLTIP_GAP_PX = 1;

/**
 * Host interface for CellTruncationManager (typically DataTableAction.vue).
 *
 * @typedef {object} CellTruncationHost
 * @property {() => Element|null|undefined} getRootEl
 * @property {(index: number) => Element|null|undefined} getHeaderRef
 * @property {(fn: () => void) => void} nextTick
 * @property {() => boolean} getEnableCellTruncate
 * @property {() => boolean} getEnableCellTruncateTooltip
 * @property {() => number} getDefaultColumnMaxWidth
 * @property {() => boolean} getBulkActions
 * @property {() => boolean} getIsTableEmpty
 * @property {() => Array<object>} getComputedHeaders
 * @property {() => object|null|undefined} getScopedSlots
 * @property {(tooltip: { show: boolean, text: string, x: number, y: number }) => void} setCellTooltip
 * @property {() => { show: boolean, text: string, x: number, y: number }} getCellTooltip
 * @property {() => Element|null|undefined} [getTooltipContentEl]
 */

/**
 * Manages column max-width sync, truncation marking, slot tbody observation, and delegated tooltips.
 */
export class CellTruncationManager {
  /**
   * @param {CellTruncationHost} host
   */
  constructor(host) {
    this.host = host;
    this.truncationTimer = null;
    this.tooltipDelegationBound = false;
    this.lastTbodyContentKey = '';
    this.tbodySlotObserver = null;
    this.slotBodyRefreshTimer = null;
    this.tooltipHideTimer = null;
    this.tooltipHovering = false;
    this.tooltipContentEl = null;
    this._onTbodyMouseOver = (event) => this.onTbodyMouseOver(event);
    this._onTbodyMouseLeave = () => this.onTbodyMouseLeave();
    this._onTooltipMouseEnter = () => this.onTooltipMouseEnter();
    this._onTooltipMouseLeave = () => this.onTooltipMouseLeave();
  }

  destroy() {
    this.teardownCellTooltipDelegation();
    this.teardownTbodySlotObserver();
    this.unbindTooltipContentElement();
    if (this.truncationTimer) {
      clearTimeout(this.truncationTimer);
      this.truncationTimer = null;
    }
    if (this.tooltipHideTimer) {
      clearTimeout(this.tooltipHideTimer);
      this.tooltipHideTimer = null;
    }
  }

  init() {
    this.setupCellTooltipDelegation();
    this.lastTbodyContentKey = this.computeTbodySlotContentKey();
    this.setupTbodySlotObserver();
    this.scheduleTruncationRefresh();
  }

  onActivated() {
    this.setupTbodySlotObserver();
    this.lastTbodyContentKey = '';
    this.syncSlotBodyPresentationIfChanged();
  }

  onUpdated(hasCustomSlot, isTableEmpty) {
    if (hasCustomSlot && !isTableEmpty && !this.tbodySlotObserver) {
      this.setupTbodySlotObserver();
    }
    this.syncSlotBodyPresentationIfChanged();
  }

  onTableBecameNonEmpty() {
    this.lastTbodyContentKey = '';
    this.setupTbodySlotObserver();
    this.syncSlotBodyPresentationIfChanged();
  }

  onSlotPaginationChange() {
    this.lastTbodyContentKey = '';
    this.syncSlotBodyPresentationIfChanged();
  }

  hasCustomTbodySlot() {
    return hasCustomTbodySlot(this.host.getScopedSlots());
  }

  getTruncationOptions() {
    return {
      enableCellTruncate: this.host.getEnableCellTruncate(),
      defaultColumnMaxWidth: this.host.getDefaultColumnMaxWidth(),
    };
  }

  getHeaderColumnStyle(header) {
    return getHeaderColumnStyle(header, this.getTruncationOptions());
  }

  computeTbodySlotContentKey() {
    const rootEl = this.host.getRootEl();
    const tbody = rootEl?.querySelector('tbody.data-table-action__tbody');
    return computeTbodySlotContentKey(tbody);
  }

  notifySlotBodyContentChanged() {
    if (!this.hasCustomTbodySlot()) return;
    this.scheduleTruncationRefresh();
  }

  scheduleSlotBodyPresentationRefresh() {
    if (!this.host.getEnableCellTruncate() || !this.hasCustomTbodySlot()) return;
    clearTimeout(this.slotBodyRefreshTimer);
    this.slotBodyRefreshTimer = setTimeout(() => {
      this.slotBodyRefreshTimer = null;
      this.syncSlotBodyPresentationIfChanged();
    }, 0);
  }

  syncSlotBodyPresentationIfChanged() {
    if (!this.hasCustomTbodySlot() || this.host.getIsTableEmpty()) return;
    const key = this.computeTbodySlotContentKey();
    if (key === this.lastTbodyContentKey) return;
    this.lastTbodyContentKey = key;
    this.notifySlotBodyContentChanged();
  }

  setupTbodySlotObserver() {
    if (!this.hasCustomTbodySlot() || this.tbodySlotObserver) return;
    const rootEl = this.host.getRootEl();
    const tbody = rootEl?.querySelector('tbody.data-table-action__tbody');
    if (!tbody) return;
    this.tbodySlotObserver = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.type === 'childList')) {
        this.scheduleSlotBodyPresentationRefresh();
      }
    });
    this.tbodySlotObserver.observe(tbody, { childList: true });
  }

  teardownTbodySlotObserver() {
    if (this.tbodySlotObserver) {
      this.tbodySlotObserver.disconnect();
      this.tbodySlotObserver = null;
    }
    if (this.slotBodyRefreshTimer) {
      clearTimeout(this.slotBodyRefreshTimer);
      this.slotBodyRefreshTimer = null;
    }
  }

  setColumnMaxWidth(colValue, maxWidthPx) {
    const rootEl = this.host.getRootEl();
    if (!colValue || colValue === 'action' || !maxWidthPx || !rootEl) return;

    const maxWidth = `${maxWidthPx}px`;
    findElementsByColumnValue(rootEl, colValue).forEach((el) => {
      el.style.setProperty('max-width', maxWidth, 'important');
      if (el.tagName === 'TD') {
        el.classList.add('data-table-action__body-cell');
      }
    });
  }

  syncBodyColumnWidth(headerIndex, maxWidthPx) {
    const th = this.host.getHeaderRef(headerIndex);
    if (!th || !maxWidthPx) return;
    const colValue = th.getAttribute('data-column-value');
    if (!colValue || colValue === 'action') return;

    this.setColumnMaxWidth(colValue, maxWidthPx);

    const rootEl = this.host.getRootEl();
    const tbody = rootEl?.querySelector('tbody.data-table-action__tbody');
    if (!tbody) return;

    const matchedCells = findElementsByColumnValue(tbody, colValue, ['TD']);
    if (matchedCells.length > 0) return;

    const dataHeaders = getTruncatableDataHeaders(this.host.getComputedHeaders());
    const dataColIndex = dataHeaders.findIndex((header) => header.value === colValue);
    if (dataColIndex < 0) return;

    const bulkActions = this.host.getBulkActions();
    const styleString = `max-width: ${maxWidthPx}px !important;`;
    tbody.querySelectorAll('tr:not(.data-table-action__empty-row)').forEach((row) => {
      const dataCells = getRowDataCells(row, { bulkActions });
      const cell = dataCells[dataColIndex];
      if (cell) {
        cell.setAttribute('style', styleString);
        cell.classList.add('data-table-action__body-cell');
      }
    });
  }

  markBodyCells() {
    const rootEl = this.host.getRootEl();
    const tbody = rootEl?.querySelector('tbody.data-table-action__tbody');
    if (!tbody) return;

    const bulkActions = this.host.getBulkActions();
    const dataHeaders = getTruncatableDataHeaders(this.host.getComputedHeaders());

    tbody.querySelectorAll('tr:not(.data-table-action__empty-row)').forEach((row) => {
      getRowDataCells(row, { bulkActions }).forEach((cell) => {
        cell.classList.add('data-table-action__body-cell');
        if (!cell.getAttribute('data-column-value')) {
          const dataCells = getRowDataCells(row, { bulkActions });
          const cellIndex = dataCells.indexOf(cell);
          if (cellIndex >= 0 && dataHeaders[cellIndex]) {
            cell.setAttribute('data-column-value', dataHeaders[cellIndex].value);
          }
        }
      });
    });
  }

  syncAllBodyColumnWidths() {
    if (!this.host.getEnableCellTruncate() || this.host.getIsTableEmpty()) return;

    this.markBodyCells();
    const truncationOptions = this.getTruncationOptions();

    this.host.getComputedHeaders().forEach((header, index) => {
      if (!header.visible || header.value === 'action' || header.noTruncate) return;
      const th = this.host.getHeaderRef(index);
      if (!th) return;

      const maxWidthPx = resolveHeaderMaxWidthPx(th, header, truncationOptions);
      if (maxWidthPx) {
        th.style.setProperty('max-width', `${maxWidthPx}px`, 'important');
        this.setColumnMaxWidth(header.value, maxWidthPx);
        this.syncBodyColumnWidth(index, maxWidthPx);
      }
    });
  }

  applyTruncationTooltips() {
    if (!this.host.getEnableCellTruncate()) return;
    const rootEl = this.host.getRootEl();
    if (!rootEl) return;

    const enableTooltip = this.host.getEnableCellTruncateTooltip();

    rootEl.querySelectorAll(TRUNCATABLE_CELL_SELECTOR).forEach((el) => {
      if (shouldSkipTruncationElement(el)) return;

      const text = (el.textContent || '').trim();
      if (!text) {
        el.removeAttribute('title');
        el.removeAttribute(TRUNCATE_TEXT_ATTR);
        return;
      }

      el.setAttribute(TRUNCATE_TEXT_ATTR, text);
      el.classList.add('data-table-action__truncatable');

      if (isTruncatedElement(el)) {
        if (!enableTooltip) {
          el.setAttribute('title', text);
        }
      } else if (!enableTooltip) {
        el.removeAttribute('title');
      }
    });
  }

  scheduleTruncationRefresh() {
    if (!this.host.getEnableCellTruncate() || this.host.getIsTableEmpty()) return;
    clearTimeout(this.truncationTimer);
    this.truncationTimer = setTimeout(() => {
      this.host.nextTick(() => {
        this.syncAllBodyColumnWidths();
        this.applyTruncationTooltips();
      });
    }, TRUNCATION_REFRESH_MS);
  }

  setupCellTooltipDelegation() {
    if (
      !this.host.getEnableCellTruncate()
      || !this.host.getEnableCellTruncateTooltip()
      || this.tooltipDelegationBound
    ) {
      return;
    }
    const rootEl = this.host.getRootEl();
    const tbody = rootEl?.querySelector('tbody.data-table-action__tbody');
    if (!tbody) return;
    tbody.addEventListener('mouseover', this._onTbodyMouseOver);
    tbody.addEventListener('mouseleave', this._onTbodyMouseLeave);
    this.tooltipDelegationBound = true;
  }

  teardownCellTooltipDelegation() {
    if (!this.tooltipDelegationBound) return;
    const rootEl = this.host.getRootEl();
    const tbody = rootEl?.querySelector('tbody.data-table-action__tbody');
    if (tbody) {
      tbody.removeEventListener('mouseover', this._onTbodyMouseOver);
      tbody.removeEventListener('mouseleave', this._onTbodyMouseLeave);
    }
    this.tooltipDelegationBound = false;
    this.hideCellTooltip();
  }

  onTbodyMouseOver(event) {
    const rootEl = this.host.getRootEl();
    const target = findTruncatableTarget(event.target, rootEl);
    if (!target || !isTruncatedElement(target)) {
      this.hideCellTooltip();
      return;
    }
    clearTimeout(this.tooltipHideTimer);
    const text = (target.getAttribute(TRUNCATE_TEXT_ATTR) || target.textContent || '').trim();
    if (!text) {
      this.hideCellTooltip();
      return;
    }
    const rect = target.getBoundingClientRect();
    this.host.setCellTooltip({
      show: true,
      text,
      x: rect.left + rect.width / 2,
      // Always anchor below the cell so the tooltip never covers truncated text.
      y: rect.bottom + TOOLTIP_GAP_PX,
    });
  }

  onTbodyMouseLeave() {
    this.scheduleHideCellTooltip();
  }

  onTooltipMouseEnter() {
    this.tooltipHovering = true;
    clearTimeout(this.tooltipHideTimer);
  }

  onTooltipMouseLeave() {
    this.tooltipHovering = false;
    this.scheduleHideCellTooltip();
  }

  scheduleHideCellTooltip() {
    clearTimeout(this.tooltipHideTimer);
    this.tooltipHideTimer = setTimeout(() => {
      if (!this.tooltipHovering) {
        this.hideCellTooltip();
      }
    }, TOOLTIP_HIDE_DELAY_MS);
  }

  bindTooltipContentElement(el) {
    this.unbindTooltipContentElement();
    if (!el) return;
    this.tooltipContentEl = el;
    el.addEventListener('mouseenter', this._onTooltipMouseEnter);
    el.addEventListener('mouseleave', this._onTooltipMouseLeave);
  }

  unbindTooltipContentElement() {
    if (!this.tooltipContentEl) return;
    this.tooltipContentEl.removeEventListener('mouseenter', this._onTooltipMouseEnter);
    this.tooltipContentEl.removeEventListener('mouseleave', this._onTooltipMouseLeave);
    this.tooltipContentEl = null;
    this.tooltipHovering = false;
  }

  hideCellTooltip() {
    clearTimeout(this.tooltipHideTimer);
    const current = this.host.getCellTooltip();
    if (!current.show) return;
    this.unbindTooltipContentElement();
    this.host.setCellTooltip({ ...current, show: false });
  }

  /**
   * @param {CellTruncationHost} host
   * @returns {CellTruncationManager}
   */
  static create(host) {
    return new CellTruncationManager(host);
  }
}
